import * as THREE from 'three';

/**
 * Particle类 - 粒子数据结构
 * 
 * 职责:
 * - 定义粒子的所有属性（位置、速度、颜色、大小、生命值等）
 * - 提供粒子初始化和重置方法
 * 
 * 验证需求: Requirements 4.1, 4.2, 4.3, 4.4
 */
class Particle {
  constructor() {
    // 位置
    this.position = new THREE.Vector3(0, 0, 0);
    
    // 速度
    this.velocity = new THREE.Vector3(0, 0, 0);
    
    // 加速度
    this.acceleration = new THREE.Vector3(0, 0, 0);
    
    // 颜色
    this.color = new THREE.Color(1, 1, 1);
    
    // 大小
    this.size = 0.2;
    
    // 生命周期
    this.life = 1.0;        // 当前生命值 (0-1)
    this.maxLife = 3.0;     // 最大生命值（秒）
    this.age = 0;           // 当前年龄（秒）
    
    // 物理属性
    this.mass = 1.0;
    this.drag = 0.99;       // 空气阻力系数
    
    // 视觉属性
    this.opacity = 1.0;
    this.emissive = new THREE.Color(1, 1, 1);
    
    // 拖尾
    this.trail = null;
    
    // 状态
    this.active = false;
  }

  /**
   * 初始化粒子
   * @param {Object} config - 粒子配置
   * @param {THREE.Vector3} config.position - 初始位置
   * @param {THREE.Vector3} config.velocity - 初始速度
   * @param {THREE.Color} config.color - 颜色
   * @param {number} config.size - 大小
   * @param {number} config.maxLife - 最大生命值
   */
  init(config) {
    // 位置
    if (config.position) {
      this.position.copy(config.position);
    }
    
    // 速度
    if (config.velocity) {
      this.velocity.copy(config.velocity);
    }
    
    // 颜色
    if (config.color) {
      if (config.color instanceof THREE.Color) {
        this.color.copy(config.color);
        this.emissive.copy(config.color);
      } else {
        this.color.set(config.color);
        this.emissive.set(config.color);
      }
    }
    
    // 大小
    if (config.size !== undefined) {
      this.size = config.size;
    }
    
    // 生命值
    if (config.maxLife !== undefined) {
      this.maxLife = config.maxLife;
    }
    
    // 重置生命周期
    this.life = 1.0;
    this.age = 0;
    this.opacity = 1.0;
    
    // 重置加速度
    this.acceleration.set(0, 0, 0);
    
    // 激活粒子
    this.active = true;
  }

  /**
   * 更新粒子状态
   * @param {number} deltaTime - 时间增量（秒）
   */
  update(deltaTime) {
    if (!this.active) return;
    
    // 更新年龄
    this.age += deltaTime;
    
    // 更新生命值（0-1）
    this.life = 1.0 - (this.age / this.maxLife);
    
    // 更新不透明度（随生命值衰减）
    this.opacity = Math.max(0, this.life);
    
    // 生命值耗尽时标记为非活跃
    if (this.life <= 0) {
      this.active = false;
    }
  }

  /**
   * 重置粒子到初始状态
   */
  reset() {
    // 重置位置和速度
    this.position.set(0, 0, 0);
    this.velocity.set(0, 0, 0);
    this.acceleration.set(0, 0, 0);
    
    // 重置颜色
    this.color.set(1, 1, 1);
    this.emissive.set(1, 1, 1);
    
    // 重置大小
    this.size = 0.2;
    
    // 重置生命周期
    this.life = 1.0;
    this.maxLife = 3.0;
    this.age = 0;
    
    // 重置视觉属性
    this.opacity = 1.0;
    
    // 清除拖尾
    if (this.trail) {
      this.trail.dispose();
      this.trail = null;
    }
    
    // 标记为非活跃
    this.active = false;
  }

  /**
   * 获取粒子的速度大小
   * @returns {number}
   */
  getSpeed() {
    return this.velocity.length();
  }

  /**
   * 检查粒子是否应该有拖尾效果
   * @returns {boolean}
   */
  shouldHaveTrail() {
    // 速度较大的粒子才有拖尾
    return this.getSpeed() > 5.0 && this.size > 0.3;
  }
}

export default Particle;
